import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import type { RoomApi } from '../net/useRoom.ts';
import { useSpotifyPlayer, type SpotifyPlayerApi } from './useSpotifyPlayer.ts';

/**
 * Haelt den Spotify-Player fuer die gesamte Sitzung.
 *
 * Warum als Provider und nicht direkt in der Bedien-Komponente:
 * Das Spotify-Geraet existiert nur, solange die Komponente montiert
 * ist, die das SDK haelt. Lag der Player in einem Panel, das nur in
 * einer bestimmten Ansicht gerendert wird, riss der Wechsel zur
 * TV-Ansicht das Geraet ab -- und mitten im Spiel verstummte die Musik.
 *
 * Der Provider sitzt deshalb ueber dem Ansichtswechsel. Die
 * Bedien-Elemente greifen nur noch lesend/steuernd darauf zu.
 */

const SpeakerContext = createContext<SpotifyPlayerApi | null>(null);

export function SpeakerProvider({ api, children }: { api: RoomApi; children: ReactNode }) {
  // Nur die konkreten Funktionen, nicht das ganze `api`-Objekt --
  // dessen Identitaet wechselt bei jeder Zustandsaenderung.
  const { registerAudio, unregisterAudio } = api;

  const onReady = useCallback(
    (deviceId: string) => {
      registerAudio(deviceId);
    },
    [registerAudio],
  );

  const onGone = useCallback(() => {
    unregisterAudio();
  }, [unregisterAudio]);

  const player = useSpotifyPlayer({ onReady, onGone });

  // Meldet das SDK einen Token-Fehler, ist die Lautsprecher-Rolle hin.
  useEffect(() => {
    if (player.status === 'error' && player.errorMessage === 'token_unavailable') {
      unregisterAudio();
    }
  }, [player.status, player.errorMessage, unregisterAudio]);

  return <SpeakerContext.Provider value={player}>{children}</SpeakerContext.Provider>;
}

export function useSpeaker(): SpotifyPlayerApi {
  const context = useContext(SpeakerContext);
  if (!context) {
    throw new Error('useSpeaker muss innerhalb von SpeakerProvider verwendet werden');
  }
  return context;
}
